import ArrowRightAltIcon from '@mui/icons-material/ArrowRightAlt'
import {
  Box,
  CircularProgress,
  IconButton,
  Paper,
  styled,
  Table,
  TableBody,
  TableContainer,
  Tooltip
} from '@mui/material'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { FC, useRef, useState } from 'react'

import ApproveIcon from '@/assets/icons/actions/approve.svg'
import AuditTrail from '@/assets/icons/actions/audit-trail.svg'
import DenyIcon from '@/assets/icons/actions/deny.svg'
import EditIcon from '@/assets/icons/actions/edit.svg'
import ViewIcon from '@/assets/icons/actions/view.svg'
import { ApproveDISModal } from '@/components/modal/ApproveDISModal'
import { ApproveDISWithGatePassModal } from '@/components/modal/ApproveDISWithGatePassModal'
import { ApproveModal } from '@/components/modal/ApproveModal'
import { DenyModal } from '@/components/modal/DenyModal'
import { useMaintenanceTicketsContext } from '@/context/MaintenanceTicketsContext'
import { useProcessingVisualizedTable } from '@/hooks/shared/useProcessingVisualizedTable'
import { useForm } from '@/utilities'
import { TicketsData } from '@/utilities/types/table'
import { SortableTableProps } from '@/utilities/types/table-props'

import { PaginatedTableFooter } from '../PaginatedTableFooter'
import { TableHeader } from '../TableHeader'

export const StyledRow = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
  height: '52px',
  padding: '16px',
  '&:nth-of-type(even)': {
    backgroundColor: '#f5f5f5',
  },
  '&:nth-of-type(odd)': {
    backgroundColor: 'white',
  },
}))

export const StyledCell = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  wordBreak: 'break-word',
  textAlign: 'center',
  fontSize: '12px',
  [theme.breakpoints.down('sm')]: {
    fontSize: '10px',
  },
}))

interface ProcessingTableProps extends SortableTableProps<TicketsData> {}

export const MaintenanceTicketsForProcessingTable: FC<ProcessingTableProps> = ({
  data = [],
  filterState,
  isLoading,
  error,
  onSort,
  page,
  setPage,
  rowsPerPage,
  setRowsPerPage,
  totalElem,
  role,
  handleDisplay,
  handleDisplayAudit,
  handleUpdateModal,
  handleSort,
}) => {
  const columns = [
    { id: 'ticketNo', label: 'Ticket Number', sortable: true, width: '10%' },
    { id: 'dealerName', label: 'Dealer', sortable: true, width: '7.5%' },
    { id: 'branch_name', label: 'Branch', sortable: true, width: '7.5%' },
    {
      id: 'typeOfConcern',
      label: 'Type of Concern',
      sortable: true,
      width: '7.5%',
    },
    {
      id: 'methodOfRepair',
      label: 'Method of Repair',
      sortable: true,
      width: '7.5%',
    },
    { id: 'dateCreated', label: 'Date Created', sortable: true, width: '10%' },
    {
      id: 'dateModified',
      label: 'Date Modified',
      sortable: true,
      width: '10%',
    },
    {
      id: 'ticketAgeInDays',
      label: 'Ticket Age',
      sortable: true,
      width: '5%',
    },
    {
      id: 'currentLayer',
      label: 'Current Assignee Next Assignee',
      sortable: false,
      width: '12.5%',
    },
    {
      id: 'statusName',
      label: 'Status',
      sortable: true,
      width: '10%',
    },
    {
      id: 'action',
      label: 'Action',
      sortable: false,
      align: 'center',
      width: '12.5%',
    },
  ]

  const parentRef = useRef<HTMLDivElement>(null)
  const [showNoResults, setShowNoResults] = useState(false)

  const {
    dispatch,
    mutateForProcessing,
    actions: {
      approveMaintenanceTicket,
      denyMaintenanceTicket,
      reviseMaintenanceTicket,
      approveCoCMaintenanceTicket,
      softDeleteMaintenanceTicket,
    },
  } = useMaintenanceTicketsContext()

  const {
    formData: approveData,
    showModal: showApprove,
    handleUpdateData: handleShowApprove,
    handleCloseModal: handleCloseApprove,
  } = useForm()

  const {
    formData: approveDISData,
    showModal: showApproveDIS,
    handleUpdateData: handleShowApproveDIS,
    handleCloseModal: handleCloseApproveDIS,
  } = useForm()

  const {
    formData: approveDISWithGatePassData,
    showModal: showApproveDISWithGatePass,
    handleUpdateData: handleShowApproveDISWithGatePass,
    handleCloseModal: handleCloseApproveDISWithGatePass,
  } = useForm()

  const {
    formData: denyData,
    showModal: showDeny,
    handleUpdateData: handleShowDeny,
    handleCloseModal: handleCloseDeny,
  } = useForm()

  const { paginatedItems, handlePageChange, handleRowsPerPageChange } =
    useProcessingVisualizedTable({
      data,
      filterState,
      containerRef: parentRef,
      page,
      setPage,
      rowsPerPage,
      setRowsPerPage,
      totalElem,
    })

  const handleApproveTicket = async (
    ticketId: number,
    note: string,
    cocDate?: any,
    projectDate?: any,
    shouldShowUpdate?: any,
  ) => {
    dayjs.extend(utc)

    let cocData = {
      dateCocReceived:
        cocDate &&
        typeof cocDate === 'object' &&
        typeof cocDate.toDate === 'function'
          ? dayjs(cocDate).utc(true).toISOString()
          : null,
      dateCompletion:
        projectDate &&
        typeof projectDate === 'object' &&
        typeof projectDate.toDate === 'function'
          ? dayjs(projectDate).utc(true).toISOString()
          : null,
    }

    let data = {
      ticketId: ticketId,
      note: note,
      dateCocReceived:
        cocDate &&
        typeof cocDate === 'object' &&
        typeof cocDate.toDate === 'function'
          ? dayjs(cocDate).utc(true).toISOString()
          : null,
      dateCompletion:
        projectDate &&
        typeof projectDate === 'object' &&
        typeof projectDate.toDate === 'function'
          ? dayjs(projectDate).utc(true).toISOString()
          : null,
    }

    if (shouldShowUpdate) {
      await approveCoCMaintenanceTicket(ticketId, cocData)(dispatch)
    } else {
      await approveMaintenanceTicket(data)(dispatch)
    }

    mutateForProcessing()
    handleCloseApprove()
    handleCloseApproveDIS()
    handleCloseApproveDISWithGatePass()
  }

  const handleDenyTicket = async (ticketId: number, note: string) => {
    let data = {
      ticketId: ticketId,
      note: note,
    }
    await denyMaintenanceTicket(data)(dispatch)
    mutateForProcessing()
    handleCloseDeny()
  }

  const handleDeleteTicket = async (ticketId: number, note: string) => {
    let data = {
      ticketId: ticketId,
      note: note,
    }
    await softDeleteMaintenanceTicket(data)(dispatch)
    mutateForProcessing()
    handleCloseDeny()
  }

  const handleReviseTicket = async (
    ticketId: number,
    note: string,
    selectedUser: number,
  ) => {
    let data = {
      ticketId: ticketId,
      note: note,
      sourceUserId: selectedUser,
    }

    await reviseMaintenanceTicket(data)(dispatch)
    mutateForProcessing()
    handleCloseDeny()
  }

  const getStatusStyle = (row: TicketsData) => {
    const { status, workFlowId, currentLayerId } = row

    const statusMap: Record<string, { label: string; color: string }> = {
      ON_HOLD: { label: 'ON HOLD', color: '#21A0B3' },
      PENDING: { label: 'FOR APPROVAL', color: '#FFB12F' },
      FOR_APPROVAL: { label: 'FOR APPROVAL', color: '#FFB12F' },
      FOR_REPLY_LETTER_QUOTATION_UPLOAD: {
        label: 'FOR REPLY LETTER UPLOAD',
        color: '#6743EE',
      },
      APPROVED: { label: 'APPROVED', color: '#379F86' },
      FOR_DEALERS_APPROVAL: { label: 'FOR DEALERS APPROVAL', color: '#6743EE' },
      FOR_REVISION: { label: 'FOR REVISION', color: '#FFB12F' },
      FOR_COC_UPLOADING: { label: 'FOR COC', color: '#6743EE' },
      PENDING_COC: { label: 'PENDING COC', color: '#6743EE' },
      CLOSED: { label: 'CLOSED', color: '#444862' },
      REJECTED: { label: 'REJECTED', color: '#E74A59' },
      FOR_CONTRACTOR_QUOTATION_UPLOADING: {
        label: 'FOR CONTRACTOR QUOTATION',
        color: '#6743EE',
      },
      FOR_PO_QUOTATION_UPLOADING: {
        label: 'FOR PO RELEASE',
        color: '#6743EE',
      },
      FOR_COC_DATE_AND_COMPLETION_DATE_INPUT: {
        label: 'FOR COC DATE COMPLETION',
        color: '#21A0B3',
      },
      FOR_SCHEDULE: {
        label: 'FOR SCHEDULE',
        color: '#6743EE',
      },
    }

    const label = statusMap[status]?.label || status || ''
    const statusName = String(row.statusName ?? '')
    const isRevision =
      status === 'FOR_REVISION' ||
      label.toLowerCase().includes('revision') ||
      statusName.toLowerCase().includes('revision')

    const words = label.split(' ')
    const shortenedLabel =
      words.length > 2 ? `${words.slice(0, 2).join(' ')}...` : label

    return {
      label: shortenedLabel,
      color: statusMap[status]?.color || 'transparent',
      fullLabel: label,
      textColor: isRevision ? '#BF092F' : 'white',
    }
  }

  const isRevisionRow = (row: TicketsData) => {
    const status = String(row.status ?? '')
    const statusName = String(row.statusName ?? '')
    return (
      status.toLowerCase().includes('revision') ||
      statusName.toLowerCase().includes('revision')
    )
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          height: '70vh',
          width: '100%',
          background: 'white',
          zIndex: 1,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          borderBottomLeftRadius: '10px',
          borderBottomRightRadius: '10px',
          marginTop: '2px',
        }}
      >
        <CircularProgress />
      </Box>
    )
  }

  if (error) {
    return (
      <Box sx={{ p: 4, textAlign: 'center', color: 'error.main' }}>
        Error: {error instanceof Error ? error.message : 'Unknown error'}
      </Box>
    )
  }

  return (
    <div
      style={{
        marginTop: '2px',
        boxShadow: 'none',
        width: '100%',
        zIndex: 1,
      }}
    >
      <TableContainer component={Paper}>
        <Table
          sx={{ minWidth: 650, minHeight: '500px' }}
          aria-label="simple table"
        >
          <TableHeader
            columns={columns}
            filterState={filterState}
            onSort={onSort}
            handleSort={handleSort}
          />
          <TableBody
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'flex-start',
            }}
          >
            {paginatedItems?.length > 0 ? (
              paginatedItems?.map((row, index) => {
                const isRevision = isRevisionRow(row)

                return (
                  <StyledRow
                    key={index}
                    sx={{ color: isRevision ? '#E74A59' : 'inherit' }}
                  >
                    {columns.map((column) => {
                    if (column.id === 'ticketNo') {
                      return (
                        <StyledCell style={{ width: column.width }}>
                          {row.ticketNo}
                        </StyledCell>
                      )
                    }

                    if (column.id === 'statusName') {
                      const { label, color, fullLabel, textColor } =
                        getStatusStyle(row)

                      return (
                        <StyledCell
                          key={`${column.id}-${row.ticketNo}`}
                          width={column.width}
                        >
                          <Tooltip title={fullLabel} arrow>
                            <Box
                              sx={{
                                backgroundColor: color,
                                color: textColor,
                                padding: '4px 8px',
                                borderRadius: '4px',
                                textAlign: 'center',
                                width: '120px',
                                fontFamily: 'Gabarito',
                                fontWeight: 500,
                                fontSize: '12px',
                                whiteSpace: 'nowrap',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                cursor: 'pointer',
                              }}
                            >
                              {label}
                            </Box>
                          </Tooltip>
                        </StyledCell>
                      )
                    }
                    // if (column.id === 'shop') {
                    //   return (
                    //     <StyledCell width={column.width}>{row.shop}</StyledCell>
                    //   )
                    // }
                    if (column.id === 'branch_name') {
                      return (
                        <StyledCell width={column.width}>
                          {row.branchName || '-'}
                        </StyledCell>
                      )
                    }
                    if (column.id === 'dealerName') {
                      return (
                        <StyledCell width={column.width}>
                          {row.dealerName}
                        </StyledCell>
                      )
                    }
                    if (column.id === 'methodOfRepair') {
                      return (
                        <StyledCell width={column.width}>
                          {row.methodOfRepair.replace(/^C\/O/, 'c/o')}
                        </StyledCell>
                      )
                    }
                    if (column.id === 'dateCreated') {
                      return (
                        <StyledCell
                          width={column.width}
                          style={{ display: 'flex', flexDirection: 'column' }}
                        >
                          <span>
                            {new Date(row.dateCreated).toLocaleDateString(
                              'en-US',
                              {
                                month: 'short',
                                day: '2-digit',
                                year: 'numeric',
                              },
                            )}
                          </span>
                          <span>
                            {new Date(row.dateCreated).toLocaleTimeString(
                              'en-US',
                              {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true,
                              },
                            )}
                          </span>
                        </StyledCell>
                      )
                    }

                    if (column.id === 'dateModified') {
                      return (
                        <StyledCell
                          width={column.width}
                          style={{ display: 'flex', flexDirection: 'column' }}
                        >
                          <span>
                            {new Date(row.dateModified).toLocaleDateString(
                              'en-US',
                              {
                                month: 'short',
                                day: '2-digit',
                                year: 'numeric',
                              },
                            )}
                          </span>
                          <span>
                            {new Date(row.dateModified).toLocaleTimeString(
                              'en-US',
                              {
                                hour: '2-digit',
                                minute: '2-digit',
                                hour12: true,
                              },
                            )}
                          </span>
                        </StyledCell>
                      )
                    }
                    if (column.id === 'ticketAgeInDays') {
                      return (
                        <StyledCell width={column.width}>
                          {row.ticketAgeInDays}
                        </StyledCell>
                      )
                    }
                    if (column.id === 'typeOfConcern') {
                      return (
                        <StyledCell width={column.width}>
                          {row.typeOfConcern}
                        </StyledCell>
                      )
                    }

                    if (column.id === 'currentLayer') {
                      return (
                        <Tooltip
                          title={`${row.assignedTo} → ${row.assignedNext}`}
                        >
                          <StyledCell
                            width={column.width}
                            sx={{
                              cursor: 'default',
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                              padding: '12px 16px',
                            }}
                          >
                            <Box
                              sx={{
                                display: 'grid',
                                gridTemplateColumns: '1fr auto 1fr',
                                alignItems: 'center',
                                gap: 0.5,
                                width: '100%',
                                textAlign: 'center',
                              }}
                            >
                              <Box>{row.aliasAssignedTo}</Box>
                              <ArrowRightAltIcon
                                sx={{
                                  fontSize: '1rem',
                                  color: 'text.secondary',
                                }}
                              />
                              <Box>{row.aliasAssignedNext}</Box>
                            </Box>
                          </StyledCell>
                        </Tooltip>
                      )
                    }

                    if (column.id === 'action') {
                      const canAct = role === row.assignedTo
                      return (
                        <StyledCell
                          key={`${column.id}-${row.ticketNo ?? row.id}`}
                          width={column.width}
                        >
                          <IconButton
                            sx={{
                              padding: '4px',
                              '&:hover': { bgcolor: 'rgba(73, 90, 255, 0.05)' },
                            }}
                            onClick={() => handleDisplay?.(row)}
                          >
                            <img
                              src={ViewIcon}
                              alt="View"
                              width={16}
                              height={16}
                            />
                          </IconButton>

                          {canAct ? (
                            <>
                              {[
                                'Area Controller',
                                'Maintenance Engineer',
                                'Maintenance Admin',
                              ].includes(role ?? '') &&
                                ![
                                  'CLOSED',
                                  'DENIED',
                                  'REJECTED',
                                  'FOR_COC_UPLOADING',
                                  'FOR_SCHEDULE',
                                  'FOR_COC_DATE_AND_COMPLETION_DATE_INPUT',
                                ].includes(row.status) && (
                                  <IconButton
                                    sx={{
                                      padding: '4px',
                                      '&:hover': {
                                        bgcolor: 'rgba(73, 90, 255, 0.05)',
                                      },
                                    }}
                                    onClick={() => handleUpdateModal?.(row)}
                                  >
                                    <img
                                      src={EditIcon}
                                      alt="Edit"
                                      width={16}
                                      height={16}
                                    />
                                  </IconButton>
                                )}
                             {/* Approve Button */}
                              {!['CLOSED', 'DENIED', 'REJECTED'].includes(row.status) &&
                                !['DND Purchasing Admin', 'GA Purchasing Staff', 'GA Manager', 'GA Purchasing Supervisor'].includes(role ?? '') && (
                                  <IconButton
                                    sx={{
                                      padding: '4px',
                                      '&:hover': { bgcolor: 'rgba(73, 90, 255, 0.05)' },
                                    }}
                                    onClick={() => {
                                      if (
                                        (row.currentLayerId === 4 &&
                                          role === 'Maintenance Admin' &&
                                          row.workFlowId === 5) ||
                                        (row.currentLayerId === 8 &&
                                          role === 'Maintenance Admin' &&
                                          row.workFlowId === 4)
                                      ) {
                                        handleShowApproveDIS(row)
                                      } else if (
                                        row.currentLayerId === 10 &&
                                        row.workFlowId === 5 &&
                                        role === 'Maintenance Admin'
                                      ) {
                                        handleShowApproveDISWithGatePass(row)
                                      } else {
                                        handleShowApprove(row)
                                      }
                                    }}
                                  >
                                    <img src={ApproveIcon} alt="Approve" width={16} height={16} />
                                  </IconButton>
                              )}

                              {/* Deny Button */}
                              {!['CLOSED', 'DENIED', 'REJECTED'].includes(row.status) && (
                                <IconButton
                                  sx={{
                                    padding: '4px',
                                    '&:hover': { bgcolor: 'rgba(73, 90, 255, 0.05)' },
                                  }}
                                  onClick={() => handleShowDeny(row)}
                                >
                                  <img src={DenyIcon} alt="Deny" width={16} height={16} />
                                </IconButton>
                              )}
                            </>
                          ) : (
                            <IconButton
                              sx={{
                                padding: '4px',
                                '&:hover': {
                                  bgcolor: 'rgba(73, 90, 255, 0.05)',
                                },
                              }}
                              onClick={() => {
                                if (handleDisplayAudit) {
                                  handleDisplayAudit(row.id)
                                }
                              }}
                            >
                              <img
                                src={AuditTrail}
                                alt="Audit"
                                width={16}
                                height={16}
                              />
                            </IconButton>
                          )}
                        </StyledCell>
                      )
                    }
                    })}
                  </StyledRow>
                )
              })
            ) : (
              <StyledRow>
                <StyledCell
                  sx={{
                    width: '100%',
                    justifyContent: 'center',
                    alignItems: 'center',
                    display: 'flex',
                    color: '#888',
                    fontSize: '14px',
                    fontStyle: 'italic',
                    height: '52px',
                  }}
                >
                  No tickets found.
                </StyledCell>
              </StyledRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <PaginatedTableFooter
        rowsPerPage={rowsPerPage}
        totalCount={totalElem}
        page={page}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
      />
      {showApproveDIS && (
        <ApproveDISModal
          data={approveDISData}
          open={showApproveDIS}
          onClose={handleCloseApproveDIS}
          onApprove={handleApproveTicket}
        />
      )}
      {showApproveDISWithGatePass && (
        <ApproveDISWithGatePassModal
          data={approveDISWithGatePassData}
          open={showApproveDISWithGatePass}
          onClose={handleCloseApproveDISWithGatePass}
          onApprove={handleApproveTicket}
          mutate={mutateForProcessing}
        />
      )}

      {showApprove && (
        <ApproveModal
          data={approveData}
          open={showApprove}
          onClose={handleCloseApprove}
          onApprove={handleApproveTicket}
          mutate={mutateForProcessing}
        />
      )}
      {showDeny && (
        <DenyModal
          data={denyData}
          open={showDeny}
          onClose={handleCloseDeny}
          onDeny={handleDenyTicket}
          onDelete={handleDeleteTicket}
          onRevise={handleReviseTicket}
          mutate={mutateForProcessing}
        />
      )}
    </div>
  )
}
